import styled from "styled-components";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import SelectionBar from "../../../../selection_bar/SelectionBar";
import TourGuidesBarContent from "../../bar/TourGuidesBarContent";
import {
  useFetchEntities,
  useGetExternalAssignmentsByDatePaginated,
} from "../../../../../reactQueryHooks";
import TourGroupsDateSectionItem from "./TourGroupsDateSectionItem";
import { CircularProgress, Button, IconButton, Tooltip } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import OptionsDrawerContent from "./OptionsDrawerContent";
import ProposalButton from "./ProposalButton";
import BatchActionsButton from "./BatchActionsButton";

//----------------------------------------------------------------

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #ffffff;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 0 0 0 5px;
  position: relative;
  overflow: hidden;
`;

const ListContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
`;

const ButtonsContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 10px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

//------------------------------------------------------------------------

const ExternalAssignmentsContent = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMultiSelectMode, setIsMultiSelectMode] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<
    Array<{
      option_id: string;
      start_time_id: string;
      date: string;
      tour_group_id: string;
      product_id: string;
    }>
  >([]);

  const { data: entitiesData } = useFetchEntities() as {
    data: { data: any };
  };

  const onlyUnassigned = searchParams.get("onlyUnassigned") === "true";
  const startDate =
    searchParams.get("startDate") || moment(new Date()).format("YYYY-MM-DD");

  // Get user IDs from search params - memoize to prevent infinite refetches
  const userIds = useMemo(() => {
    const userIdsParam = searchParams.get("userIds");
    return userIdsParam ? userIdsParam.split(",") : undefined;
  }, [searchParams]);

  // Get product option IDs from search params - memoize to prevent infinite refetches
  const productOptionIds = useMemo(() => {
    const productOptionIdsParam = searchParams.get("productOptionIds");
    return productOptionIdsParam ? productOptionIdsParam.split(",") : undefined;
  }, [searchParams]);

  const {
    data: externalAssignments,
    isFetching,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch,
    isRefetching,
    isFetchingNextPage,
  } = useGetExternalAssignmentsByDatePaginated({
    start_date: startDate,
    only_unassigned: onlyUnassigned,
    user_ids: userIds, // Pass user IDs to the API call
    productOptionIds: productOptionIds, // Pass product option IDs to the API call
  }) as any;
  const loading =
    (isFetching || isRefetching || isLoading) && !isFetchingNextPage;

  useEffect(() => {
    refetch();
  }, [refetch, onlyUnassigned, startDate, userIds, productOptionIds]); // Add userIds and productOptionIds to dependency array

  const allData = useMemo(() => {
    return externalAssignments?.pages?.flatMap((page: any) =>
      page.data.docs.map((doc: any) => doc)
    );
  }, [externalAssignments]);

  // Flatten all tour groups from date sections for batch actions
  const allTourGroups = useMemo(() => {
    if (!allData) return [];
    return allData.flatMap((dateSection: { date: string; data: any[] }) =>
      dateSection.data || []
    );
  }, [allData]);

  const handleDateChange = (action: string) => {
    let newDate;
    if (action === "next") {
      newDate = moment(startDate).add(1, "days").format("YYYY-MM-DD");
    } else {
      newDate = moment(startDate).subtract(1, "days").format("YYYY-MM-DD");
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set("startDate", newDate);
    setSearchParams(newParams);
  };

  const handleSetDate = (date: string) => {
    const newParams = new URLSearchParams(searchParams);
    const newDate = moment(date).format("YYYY-MM-DD");
    newParams.set("startDate", newDate);
    setSearchParams(newParams);
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      // Clear selections when exiting multi-select mode
      setSelectedItems([]);
    }
  };

  const handleItemSelection = (
    item: {
      option_id: string;
      start_time_id: string;
      date: string;
      tour_group_id: string;
      product_id: string;
    },
    selected: boolean
  ) => {
    if (selected) {
      setSelectedItems((prev) => [...prev, item]);
    } else {
      setSelectedItems((prev) =>
        prev.filter(
          (selectedItem) => !(selectedItem.tour_group_id === item.tour_group_id)
        )
      );
    }
  };

  //-----------------------------------------------------------------------

  return (
    <Wrapper>
      <SelectionBar>
        <TourGuidesBarContent
          date={startDate}
          setDate={handleSetDate}
          handleDateChange={handleDateChange}
          refetch={refetch}
        />
      </SelectionBar>
      <ContentContainer>
        <ListContainer>
          {loading && (
            <LoadingContainer>
              <CircularProgress size={30} />
            </LoadingContainer>
          )}

          {allData?.map((dateSection: { date: string; data: any[] }) => {
            return (
              <TourGroupsDateSectionItem
                key={dateSection?.date}
                date={dateSection?.date}
                data={dateSection?.data}
                refetch={refetch}
                isMultiSelectMode={isMultiSelectMode}
                onItemSelection={handleItemSelection}
                selectedItems={selectedItems}
              />
            );
          })}
          {!isFetching && allData?.length === 0 && (
            <span>No assignments found for the selected period.</span>
          )}
          {hasNextPage && (
            <div style={{ textAlign: "center", padding: "10px" }}>
              <Button
                variant="contained"
                onClick={() => fetchNextPage()}
                disabled={isFetching}
              >
                {isFetching ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}

          {!loading && (
            <ButtonsContainer>
              <Tooltip title="Toggle multi-select mode" placement="left" arrow>
                <IconButton
                  onClick={toggleMultiSelectMode}
                  size="large"
                  sx={{
                    backgroundColor: isMultiSelectMode ? "#ff9f9f" : "#9dd8ff",
                    color: "#000",
                    "&:hover": {
                      backgroundColor: isMultiSelectMode
                        ? "#ff8080"
                        : "#9dd8ff",
                    },
                  }}
                >
                  {isMultiSelectMode ? <CloseIcon /> : <CheckBoxIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Open filters" placement="left" arrow>
                <IconButton
                  onClick={() => {
                    setOpenDrawer(true);
                  }}
                  size="large"
                  sx={{
                    backgroundColor: "#9dd8ff",
                    color: "#000",
                    "&:hover": {
                      backgroundColor: "#9dd8ff",
                    },
                  }}
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>

              {isMultiSelectMode && selectedItems.length > 0 && (
                <>
                  <ProposalButton
                    selectedItems={selectedItems}
                    entities={entitiesData}
                  />
                  <BatchActionsButton
                    selectedItems={selectedItems}
                    entities={entitiesData}
                    refetch={refetch}
                    allTourGroupsData={allTourGroups}
                  />
                </>
              )}
            </ButtonsContainer>
          )}
        </ListContainer>
      </ContentContainer>
      <Drawer open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <OptionsDrawerContent close={() => setOpenDrawer(false)} />
      </Drawer>
    </Wrapper>
  );
};

export default ExternalAssignmentsContent;
